/**
 * THE ONE READING OF A SAVED countdownEvents DOC that both the page and a
 * server lane need, with no Firebase import, so a Netlify Function can import
 * it as it is (the eventRsvp lane does). normalizeEvent in countdownEvents.ts
 * uses these same functions for visibility, kind and dates, and
 * tests/event-rsvp.test.mjs pins eventRsvpFacts against normalizeEvent over a
 * matrix of real saved shapes, so the two can never read one doc two ways.
 *
 * The saved shape matters here: every event model v2 field, rsvpRequired,
 * dateTbd, repeat and repeatUntil among them, lives under the ONE eventV2 map
 * on the wire (countdownEvents.ts, normalizeEvent's own comment). A reader
 * that looks for them at the top level reads every real event as "no".
 */

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isValidCountdownDate(value: unknown): value is string {
  if (typeof value !== 'string' || !DATE_RE.test(value)) return false;
  const [year, month, day] = value.split('-').map(Number);
  if (year < 1 || month < 1 || month > 12 || day < 1) return false;
  const leapYear = year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0);
  const monthDays = [31, leapYear ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  return day <= monthDays[month - 1];
}

/** Repeats (Turn 5). Absent = does not repeat. The A/B rule is the canvas's
 * own sentence: "A/B repeats follow the weeks a Director has marked.
 * Unmarked weeks are skipped, never guessed." Consumers implement that by
 * reading abWeekCalendar's marked weeks; nothing expands repeats yet. */
export const EVENT_REPEAT_CHOICES = ['weekly', 'a-weeks', 'b-weeks', 'monthly'] as const;
export type EventRepeatChoice = (typeof EVENT_REPEAT_CHOICES)[number];

export type CountdownVisibility = 'everyone' | 'comp' | 'staff';
export type CountdownKind = 'competition' | 'recital' | 'other';

/** Absent or unknown reads as 'everyone'. */
export function normalizeEventVisibility(value: unknown): CountdownVisibility {
  return value === 'comp' || value === 'staff' ? value : 'everyone';
}

/** Absent or unknown reads as 'competition', the oldest kind. */
export function normalizeEventKind(value: unknown): CountdownKind {
  return value === 'recital' || value === 'other' ? value : 'competition';
}

export interface EventRsvpFacts {
  visibility: CountdownVisibility;
  kind: CountdownKind;
  rsvpRequired: boolean;
  dateTbd: boolean;
  /** The day the event starts; '' for a date to be confirmed. */
  date: string;
  /** The last day it runs: repeatUntil, else endDate, else date. Null when it
   * repeats with no end, or has no date yet. */
  lastDate: string | null;
}

function record(value: unknown): Record<string, unknown> {
  return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}

/**
 * Read the facts an RSVP needs off a RAW saved doc, the same way
 * normalizeEvent reads them. `today` is the studio's date key, used only where
 * normalizeEvent itself falls back to today (a doc with no valid date and no
 * TBD flag).
 */
export function eventRsvpFacts(value: unknown, today: string): EventRsvpFacts {
  const raw = record(value);
  const v2 = record(raw.eventV2);
  const dateTbd = v2.dateTbd === true && !isValidCountdownDate(raw.date);
  const date = dateTbd ? '' : isValidCountdownDate(raw.date) ? raw.date : today;
  const endDate = !dateTbd && isValidCountdownDate(raw.endDate) && raw.endDate >= date ? raw.endDate : undefined;
  const repeat = !dateTbd && (EVENT_REPEAT_CHOICES as readonly unknown[]).includes(v2.repeat);
  const repeatUntil = repeat && isValidCountdownDate(v2.repeatUntil) && v2.repeatUntil >= date
    ? v2.repeatUntil
    : undefined;
  let lastDate: string | null = endDate ?? date;
  if (repeat) lastDate = repeatUntil && repeatUntil > lastDate ? repeatUntil : repeatUntil ? lastDate : null;
  if (dateTbd) lastDate = null;
  return {
    visibility: normalizeEventVisibility(raw.visibility),
    kind: normalizeEventKind(raw.kind),
    rsvpRequired: v2.rsvpRequired === true,
    dateTbd,
    date,
    lastDate,
  };
}
